import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CardMedia,
  Container,
  Stack,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import {
  AnnouncementCategory,
  announcementCategoryOptions,
} from "../../../common/enums/announcement-category";
import { AnnouncementModel } from "../api/types";
import useHomeStore from "../api/store";
import { ArrowBack, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { AddAnnouncementModal } from "./create-announcement-modal";
import { useDeleteAnnouncement, useGetAnnouncements } from "../api/hooks";
import "react-quill/dist/quill.snow.css";
import useMainStore from "../../landing-page/api/store";
import { getAnnouncementPrivileges } from "../../../common/auth/privileges";
import { useAuthStore } from "../../../common/auth/store";

const AnnouncementDetailPage: React.FC = () => {
  const navigate = useNavigate();

  const location = useLocation();
  const storeItem = useHomeStore((s) => s.selectedAnnouncement);
  const mainStore = useMainStore();
  const authStore = useAuthStore();
  const anncouncementPrivileges = getAnnouncementPrivileges(
    authStore.privileges
  );

  const userRoles = mainStore.currentUser?.roles || [];
  const isRoleAllowed =
    userRoles.includes("SuperAdmin") ||
    userRoles.includes("BizboxAdmin") ||
    userRoles.includes("BizboxUser");
  const item: AnnouncementModel | undefined =
    storeItem ?? (location.state?.item as AnnouncementModel);

  const { data, isFetching, refetch } = useGetAnnouncements();

  const setAddModal = useHomeStore((s) => s.setAddModal);
  const setSelectedAnnouncement = useHomeStore(
    (s) => s.setSelectedAnnouncement
  );
  const resetPreview = useHomeStore((s) => s.setPreviewUrl);
  const resetPreview2 = useHomeStore((s) => s.setPreviewUrl2);

  const { mutate: deleteAnnouncement } = useDeleteAnnouncement();
  const [openConfirm, setOpenConfirm] = useState(false);

  const handleEdit = () => {
    if (!item) return;
    resetPreview(null);
    resetPreview2(null);
    setSelectedAnnouncement(item);
    setAddModal(true);
  };

  const handleDelete = () => setOpenConfirm(true);
  const handleCancel = () => setOpenConfirm(false);
  const handleConfirm = () => {
    if (!item) return;
    deleteAnnouncement(Number(item.id), {
      onSuccess: () => navigate("/home"),
    });
  };

  if (isFetching) return <CircularProgress />;
  const bodyContent = typeof item?.body === "string" ? item.body : "";
  const showLastUpdated =
    !!item?.lastUpdatedByName &&
    item.lastUpdatedByName.toLowerCase() !== "unknown";
  const showCreatedBy =
    !!item?.createdByName && item.createdByName.toLowerCase() !== "unknown";

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100vh"
      ml={10}
      mr={10}
      mt={2}
    >
      <Box flex={1}>
        {/* Banner */}
        <Box
          sx={{ position: "relative", width: "100%", display: "inline-block" }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 20 }}
          >
            <IconButton
              id="notification-arrow-back"
              data-testid="notification-arrow-back"
              onClick={() => navigate(-1)}
              sx={{
                width: 32,
                height: 32,
                color: "#FF5F47",
              }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                lineHeight: "32px",
              }}
            >
              Back to Announcements
            </Typography>
          </Stack>
          <CardMedia
            component="img"
            image={item?.photoUrl ?? "/default-banner-image.png"}
            alt="Announcement Banner"
            sx={{
              width: "100%",
              height: 200,
              objectFit: "fill",
              borderRadius: "12px",
              mt: 10,
            }}
          />
          {isRoleAllowed && (
            <Stack
              direction="column"
              spacing={1}
              sx={{
                position: "absolute",
                top: 0,
                left: "100%",
                ml: 4,
                mt: 10,
              }}
            >
              <IconButton
                id={`announcement-edit-button`}
                data-testid="announcement-edit-button"
                onClick={handleEdit}
                sx={{
                  bgcolor: "white",
                  boxShadow: 2,
                  width: 32,
                  height: 32,
                  borderRadius: "12px",
                  color: "#123257",
                }}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
              <IconButton
                id={`announcement-delete-button`}
                data-testid="announcement-delete-button"
                onClick={handleDelete}
                sx={{
                  bgcolor: "white",
                  boxShadow: 2,
                  width: 32,
                  height: 32,
                  borderRadius: "12px",
                  color: "#F93B4C",
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>

        {/* Body */}
        <Container sx={{ py: 4 }}>
          {item ? (
            <Stack spacing={2}>
              {/* Category & Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1}>
                  <Typography
                    sx={{
                      color: "#FF5F47",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "12px",
                    }}
                  >
                    {getLabelFromOptions(
                      announcementCategoryOptions,
                      item.category
                    )}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#123257",
                      fontWeight: 300,
                      textTransform: "uppercase",
                      fontSize: "12px",
                    }}
                  >
                    | {dayjs(item.date).format("MM/DD/YYYY")}
                  </Typography>
                </Stack>
              </Box>

              {/* Title */}
              <Typography
                sx={{
                  color: "#123257",
                  fontWeight: 600,
                  fontSize: "24px",
                  textAlign: "left",
                }}
              >
                {item.title}
              </Typography>

              {/* Rich text body */}
              <Box sx={{ "& .ql-editor": { padding: 0, margin: 0 } }}>
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{ __html: bodyContent }}
                />
              </Box>
            </Stack>
          ) : (
            <Typography>No additional item details found.</Typography>
          )}
        </Container>

        {/* Confirmation Dialog */}
        <Dialog
          open={openConfirm}
          onClose={handleCancel}
          aria-labelledby="confirm-delete-title"
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              width: 400,
              height: 176,
              borderRadius: 4,
              p: 0,
            },
            "data-testid": "delete-announcement-confirm-dialog",
          } as any}
        >
          <DialogTitle
            id="confirm-delete-title"
            color="#123257"
            fontWeight={600}
          >
            Delete Announcement
          </DialogTitle>
          <DialogContent sx={{ px: 3, overflowY: "hidden" }}>
            <Typography variant="body2" color="textSecondary" paddingLeft={1.5}>
              Are you sure you want to delete this announcement? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              id="delete-announcement-cancel-button"
              data-testid="delete-announcement-cancel-button"
              onClick={handleCancel}
              sx={{
                borderRadius: "12px",
                color: "#123257",
                width: 85,
                height: 44,
              }}
            >
              Cancel
            </Button>
            <Button
              id="delete-announcement-confirm-button"
              data-testid="delete-announcement-confirm-button"
              disabled={!anncouncementPrivileges.delete}
              onClick={handleConfirm}
              variant="contained"
              sx={{
                borderRadius: "12px",
                backgroundColor: "#FF5F47",
                width: 85,
                height: 44,
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Box
        component="footer"
        position="sticky"
        bottom={0}
        width="100%"
        sx={{
          bgcolor: "background.paper",
          py: 1,
          px: { xs: 2, sm: 3, md: 4, lg: 10 },
          display: "flex",
          justifyContent:
            showLastUpdated && showCreatedBy
              ? "space-between"
              : showLastUpdated
              ? "flex-start"
              : "flex-end",
          alignItems: "center",
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        {showLastUpdated && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Last updated by:
            </Typography>
            {item.lastUpdatedByPhotoUrl && (
              <Avatar
                src={item.lastUpdatedByPhotoUrl}
                alt={item.lastUpdatedByName}
                sx={{ width: 24, height: 24 }}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "capitalize" }}
            >
              {item.lastUpdatedByName}
            </Typography>
          </Stack>
        )}

        {showCreatedBy && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Created by:
            </Typography>
            {item.userPhotoUrl && (
              <Avatar
                src={item.userPhotoUrl}
                alt={item.createdByName}
                sx={{ width: 24, height: 24 }}
              />
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "capitalize" }}
            >
              {item.createdByName}
            </Typography>
          </Stack>
        )}
      </Box>

      <AddAnnouncementModal
        announcementId={item?.id}
        onSaved={async () => {
          const result = await refetch();
          const fresh = result.data?.items.find((a) => a.id === item?.id);
          if (fresh) setSelectedAnnouncement(fresh);
        }}
      />
    </Box>
  );
};

const getLabelFromOptions = (
  options: { label: string; value: number }[],
  value: number | string | undefined
): string => {
  if (value == null) return "N/A";
  const enumValue =
    typeof value === "string"
      ? AnnouncementCategory[value as keyof typeof AnnouncementCategory]
      : value;
  return options.find((opt) => opt.value === enumValue)?.label || "N/A";
};
export default AnnouncementDetailPage;
